import {createAsyncThunk} from "@reduxjs/toolkit";
import {chatAPI} from "../api";
import {handlerError} from "../../../store/thunkHandlers";
import {setMessage} from "../../messages/model/slice";
import {MESSAGE_SEVERITY} from "../../../utils/const";
import {AppDispatch, RootState} from "../../../store";
import {channelOf, GENERAL, IChatMessage, IThread} from "./types";
import {permissionsOf} from "../../../models/permissions";
import {messageNotify} from "../../notify/model/actions";

type ThunkCfg = {dispatch: AppDispatch; state: RootState};

/** Ошибка API → всплывающее сообщение с текстом из `message` ответа. */
const fail = (e: unknown, dispatch: AppDispatch): string => {
    const text = handlerError(e);
    dispatch(setMessage({severity: MESSAGE_SEVERITY.error, text}));
    return text;
};

/** Последняя страница канала или (`older`) страница старше первого загруженного. */
export const fetchMessages = createAsyncThunk<
    {channel: string; older: boolean; items: IChatMessage[]},
    {channel: string; older?: boolean},
    ThunkCfg
>("chat/messages", async ({channel, older = false}, {dispatch, getState, rejectWithValue}) => {
    try {
        const first = getState().chat.channels[channel]?.messages[0];
        const cursor = older && first ? {before: first.created_at, before_id: first.id} : undefined;
        const items = await chatAPI.getMessages(channel, cursor);
        return {channel, older, items};
    } catch (e) {
        return rejectWithValue(fail(e, dispatch));
    }
});

export const fetchUnread = createAsyncThunk<Record<string, number>, void, ThunkCfg>(
    "chat/unread",
    async (_, {getState, rejectWithValue}) => {
        try {
            const counts = await chatAPI.unread();
            // Тред на экране и окно в фокусе — он прочитан; без фокуса непрочитанное копится.
            const {active} = getState().chat;
            if (active && isChannelVisible(getState(), active)) delete counts[active];
            // Нет общего чата у роли — нет и в счётчиках (S12).
            if (!permissionsOf(getState().users.current).generalChat) delete counts[GENERAL];
            return counts;
        } catch (e) {
            // Счётчики — не повод для всплывающей ошибки.
            return rejectWithValue(handlerError(e));
        }
    },
    // Без способности `unread` (ROLES.md § 4) запрос не уходит: бэк ответил бы 403.
    {condition: (_, {getState}) => permissionsOf(getState().users.current).unread},
);

export const fetchThreads = createAsyncThunk<IThread[], void, ThunkCfg>(
    "chat/threads",
    async (_, {rejectWithValue}) => {
        try {
            return await chatAPI.threads();
        } catch (e) {
            // Список перечитывается на каждое сообщение — при недоступном бэке всплывающие сыпались бы подряд.
            return rejectWithValue(handlerError(e));
        }
    },
);

/** «Обсуждения» открыты — список перечитывается целиком (он короткий, без локального пересчёта). */
const refreshThreads = (dispatch: AppDispatch, getState: () => RootState) => {
    if (getState().chat.threadsOpen) dispatch(fetchThreads());
};

export const fetchSendMessage = createAsyncThunk<
    IChatMessage,
    {channel: string; text: string; attachment_ids: string[]},
    ThunkCfg
>("chat/send", async ({channel, text, attachment_ids}, {dispatch, rejectWithValue}) => {
    try {
        return await chatAPI.send(channel, text, attachment_ids);
    } catch (e) {
        return rejectWithValue(fail(e, dispatch));
    }
});

export const fetchEditMessage = createAsyncThunk<IChatMessage, {id: string; text: string}, ThunkCfg>(
    "chat/edit",
    async ({id, text}, {dispatch, rejectWithValue}) => {
        try {
            return await chatAPI.edit(id, text);
        } catch (e) {
            return rejectWithValue(fail(e, dispatch));
        }
    },
);

export const fetchDeleteMessage = createAsyncThunk<{id: string; car_id: string | null}, string, ThunkCfg>(
    "chat/delete",
    async (id, {dispatch, rejectWithValue}) => {
        try {
            return await chatAPI.remove(id);
        } catch (e) {
            return rejectWithValue(fail(e, dispatch));
        }
    },
);

/** Поставить или снять свою реакцию. */
export const fetchToggleReaction = createAsyncThunk<
    Awaited<ReturnType<typeof chatAPI.addReaction>>,
    {message: IChatMessage; emoji: string},
    ThunkCfg
>("chat/reaction", async ({message, emoji}, {dispatch, getState, rejectWithValue}) => {
    const me = getState().users.current?.id;
    const mine = message.reactions.some((r) => r.emoji === emoji && !!me && r.user_ids.includes(me));
    try {
        return mine ? await chatAPI.removeReaction(message.id, emoji) : await chatAPI.addReaction(message.id, emoji);
    } catch (e) {
        return rejectWithValue(fail(e, dispatch));
    }
});

// ─── Прочитано: не чаще раза в 2 с на канал ───────────────────────────────

export const READ_THROTTLE_MS = 2000;
const lastRead: Record<string, number> = {};
const pendingRead: Record<string, number | undefined> = {};

/** Канал прочитан: счётчик — сразу, POST /chat/read — не чаще раза в 2 с (последний вызов не теряется). */
export const markChannelRead = (channel: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    if (!permissionsOf(getState().users.current).unread) return;
    dispatch({type: "chat/unreadCleared", payload: channel});
    if (pendingRead[channel] !== undefined) return;
    const wait = Math.max(0, (lastRead[channel] ?? 0) + READ_THROTTLE_MS - Date.now());
    pendingRead[channel] = window.setTimeout(() => {
        pendingRead[channel] = undefined;
        lastRead[channel] = Date.now();
        chatAPI.read(channel).then(
            () => refreshThreads(dispatch, getState),
            () => {
                /* не прочиталось — счётчик вернётся с /chat/unread */
            },
        );
    }, wait);
};

/** Видит ли пользователь сейчас этот канал: тред открыт, окно в фокусе. */
export const isChannelVisible = (state: RootState, channel: string): boolean =>
    state.chat.active === channel &&
    typeof document !== "undefined" &&
    document.visibilityState === "visible" &&
    document.hasFocus();

/** После переподключения: пропущенное не догоняется — каналы сбрасываются, открытый и счётчики перечитываются. */
export const chatAfterReconnect = () => (dispatch: AppDispatch, getState: () => RootState) => {
    const {active} = getState().chat;
    dispatch({type: "chat/channelsReset"});
    if (active) dispatch(fetchMessages({channel: active}));
    dispatch(fetchUnread());
};

/**
 * `chat:message`: в ленту; чужое сообщение человека — в непрочитанное или
 * сразу «прочитано», если тред на экране; системные в непрочитанное не
 * считаются (S4). Системное уведомление — по правилу notify.
 */
export const messageArrived = (m: IChatMessage, onOpen?: (channel: string) => void) => (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch({type: "chat/messageUpserted", payload: m});
    refreshThreads(dispatch, getState);
    const state = getState();
    if (m.author?.id && m.author.id === state.users.current?.id) return;
    if (m.kind !== "user") return;
    const channel = channelOf(m);
    const can = permissionsOf(state.users.current);
    // Нет общего чата у роли — не считается и не оповещает (S12).
    if (channel === GENERAL && !can.generalChat) return;
    // Без `unread` — без счётчиков, без `notifications` — без оповещений (S20.2).
    if (can.unread) {
        if (isChannelVisible(state, channel)) dispatch(markChannelRead(channel));
        else dispatch({type: "chat/unreadIncremented", payload: channel});
    }
    if (can.notifications) dispatch(messageNotify(m, onOpen));
};
